import { useEffect, useState } from 'react';
import { adminApi, authApi } from '../../services/portalApi';
import { useAuthStore } from '../../store/authStore';
import type { Estate, PatrolType } from '../../types';
import { PRICE, BILLING_NOTE } from '../../constants/pricing';

/** APK download path, served by nginx straight off /opt/patrolit/apk/.
 *
 *  Universal builds set no EXPO_PUBLIC_ESTATE_ID and learn their site from the
 *  setup link below, so `/app/patrolit.apk` serves every site. The per-site
 *  path is kept as a fallback: a site that was given a legacy build (one with
 *  the ID compiled in) still has its own file, and falls back to the shared
 *  one only if no per-site file was ever uploaded.
 *
 *  A fixed path, rather than an EAS artifact URL (those expire after ~30
 *  days): replacing the file publishes a new release with nothing here to
 *  rebuild. */
const UNIVERSAL_APK_PATH = '/app/patrolit.apk';

/** Setup page a patroller opens on their phone — it carries the site, so an
 *  admin can WhatsApp this rather than asking a guard to type a 36-character
 *  UUID (see pages/public/AppSetup.tsx and mobile/app/(auth)/onboarding.tsx).
 *
 *  NOT under /app/ — nginx aliases that prefix to the APK directory, so
 *  anything there never reaches the SPA router. */
function setupLinkFor(origin: string, estateId: string): string {
  return `${origin}/setup?site=${estateId}`;
}

function getStoredCentre(): { lat: string; lng: string } {
  try {
    const raw = localStorage.getItem('patrolit-default-centre');
    if (raw) {
      const [lat, lng] = JSON.parse(raw) as [number, number];
      return { lat: lat.toString(), lng: lng.toString() };
    }
  } catch { /* ignore */ }
  return { lat: '', lng: '' };
}

function ShowHideInput({ value, onChange, placeholder, autoComplete }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-14 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600"
        tabIndex={-1}
      >
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [estate, setEstate] = useState<Estate | null>(null);

  // Estate fields
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [contacts, setContacts] = useState('');
  const stored = getStoredCentre();
  const [defaultLat, setDefaultLat] = useState(stored.lat);
  const [defaultLng, setDefaultLng] = useState(stored.lng);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [estateError, setEstateError] = useState('');

  // Patrol types
  const [patrolTypes, setPatrolTypes] = useState<PatrolType[]>([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [typeAdding, setTypeAdding] = useState(false);
  const [typeError, setTypeError] = useState('');

  function loadPatrolTypes() {
    adminApi.listPatrolTypes().then(setPatrolTypes).catch(() => {});
  }

  // Change password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSaved, setPwSaved] = useState(false);

  useEffect(() => {
    adminApi.getEstate()
      .then((e) => {
        setEstate(e);
        setName(e.name);
        setAddress(e.address ?? '');
        setContacts(e.alarm_contacts.join('\n'));
        if (e.default_lat != null) setDefaultLat(e.default_lat.toString());
        if (e.default_lng != null) setDefaultLng(e.default_lng.toString());
      })
      .finally(() => setLoading(false));
    loadPatrolTypes();
  }, []);

  async function handleSaveEstate(e: React.FormEvent) {
    e.preventDefault();
    setEstateError('');
    setSaving(true);
    try {
      const alarm_contacts = contacts
        .split('\n')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      const updated = await adminApi.updateSettings({
        name,
        address,
        alarm_contacts,
        default_lat: defaultLat ? parseFloat(defaultLat) : null,
        default_lng: defaultLng ? parseFloat(defaultLng) : null,
      });
      setEstate(updated);

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setEstateError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError('');
    if (newPassword !== confirmPassword) { setPwError('New passwords do not match.'); return; }
    if (newPassword.length < 8) { setPwError('Password must be at least 8 characters.'); return; }
    setPwSaving(true);
    try {
      await authApi.updateMe({ current_password: currentPassword, password: newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwSaved(true);
      setTimeout(() => setPwSaved(false), 3000);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setPwError(status === 401 ? 'Current password is incorrect.' : 'Failed to change password. Please try again.');
    } finally {
      setPwSaving(false);
    }
  }

  async function handleAddType(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newTypeName.trim();
    if (!trimmed) return;
    setTypeError('');
    setTypeAdding(true);
    try {
      await adminApi.createPatrolType(trimmed);
      setNewTypeName('');
      loadPatrolTypes();
    } catch {
      setTypeError('Failed to add type. It may already exist.');
    } finally {
      setTypeAdding(false);
    }
  }

  async function handleDeleteType(id: string, name: string) {
    if (!window.confirm(`Delete patrol type "${name}"?`)) return;
    await adminApi.deletePatrolType(id);
    loadPatrolTypes();
  }

  const estateId = useAuthStore((s) => s.estateId());
  const [copied, setCopied] = useState(false);
  const [apkCopied, setApkCopied] = useState(false);
  const [setupCopied, setSetupCopied] = useState(false);

  const [sub, setSub] = useState<{ subscription_status: string; amount: string; cancellable: boolean } | null>(null);
  const [subBusy, setSubBusy] = useState(false);
  const [subError, setSubError] = useState('');

  useEffect(() => {
    adminApi.getSubscription().then(setSub).catch(() => setSub(null));
  }, []);

  async function handleCancelSubscription() {
    // Deliberately a hard confirm: this ends the subscription and cannot be
    // undone from here — restarting means signing up again.
    //
    // Says "no further payments will be taken" rather than "billing stops
    // immediately". Both are true from the customer's side, but the second
    // promises a timing we do not control: the PayFast cancellation is
    // completed by a human (see admin_subscription_cancel in
    // pt_rest_handler). Do not restore "immediately" without restoring the
    // automated API call behind it.
    if (!window.confirm(
      'Cancel your PatrolIt subscription?\n\n' +
      'No further payments will be taken. Your patrol history is kept. ' +
      'This cannot be undone from the portal.'
    )) return;
    setSubBusy(true);
    setSubError('');
    try {
      await adminApi.cancelSubscription();
      const fresh = await adminApi.getSubscription();
      setSub(fresh);
    } catch {
      setSubError('Could not cancel. Please email us and we will stop the billing.');
    } finally {
      setSubBusy(false);
    }
  }

  const [fbCategory, setFbCategory] = useState('general');
  const [fbMessage, setFbMessage] = useState('');
  const [fbSending, setFbSending] = useState(false);
  const [fbSent, setFbSent] = useState(false);
  const [fbError, setFbError] = useState('');

  async function handleSendFeedback(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = fbMessage.trim();
    if (!trimmed) return;
    setFbSending(true);
    setFbError('');
    try {
      await adminApi.sendFeedback({ category: fbCategory, message: trimmed });
      setFbMessage('');
      setFbSent(true);
      setTimeout(() => setFbSent(false), 6000);
    } catch {
      setFbError('Could not send. Please try again.');
    } finally {
      setFbSending(false);
    }
  }

  // Same-origin, resolved at render rather than hardcoded, so the links stay
  // correct on any host the portal is served from.
  const apkUrl = `${window.location.origin}${UNIVERSAL_APK_PATH}`;
  const setupUrl = estateId ? setupLinkFor(window.location.origin, estateId) : '';

  function handleCopyEstateId() {
    if (!estateId) return;
    navigator.clipboard.writeText(estateId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleCopyApkUrl() {
    if (!apkUrl) return;
    navigator.clipboard.writeText(apkUrl).then(() => {
      setApkCopied(true);
      setTimeout(() => setApkCopied(false), 2000);
    });
  }

  function handleCopySetupUrl() {
    if (!setupUrl) return;
    navigator.clipboard.writeText(setupUrl).then(() => {
      setSetupCopied(true);
      setTimeout(() => setSetupCopied(false), 2000);
    });
  }

  if (loading) return <div className="px-6 py-12 text-gray-400">Loading…</div>;

  return (
    <div className="px-6 py-6 max-w-2xl space-y-8">
      <h1 className="text-lg font-semibold text-gray-900">Settings</h1>

      {/* Estate details */}
      <section className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-800 mb-4">Site details</h2>
        <form onSubmit={handleSaveEstate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site name</label>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input type="text" value={address} onChange={(e) => setAddress(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Starting GPS coordinates</label>
            <p className="text-xs text-gray-400 mb-2">Centre of the live map when no patrollers are active.</p>
            <div className="flex gap-3">
              <input type="number" step="any" value={defaultLat} onChange={(e) => setDefaultLat(e.target.value)}
                placeholder="Latitude  e.g. -33.9249"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              <input type="number" step="any" value={defaultLng} onChange={(e) => setDefaultLng(e.target.value)}
                placeholder="Longitude  e.g. 18.4241"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Alarm email addresses</label>
            <p className="text-xs text-gray-400 mb-1">One per line. All contacts receive alarm notifications.</p>
            <textarea rows={4} value={contacts} onChange={(e) => setContacts(e.target.value)}
              placeholder={"security@estate.com\ntrustee@estate.com"}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none" />
          </div>

          {estateError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{estateError}</p>
          )}
          <div className="flex items-center gap-3">
            <button type="submit" disabled={saving}
              className="px-5 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-60 transition-colors">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {saved && <span className="text-sm text-green-600 font-medium">✓ Saved</span>}
          </div>
        </form>
      </section>

      {/* Patrol types */}
      <section className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-800 mb-4">Patrol types</h2>
        <p className="text-xs text-gray-400 mb-3">Define the types of patrol patrollers can be assigned to (e.g. Foot, Golf Cart).</p>
        {patrolTypes.length > 0 ? (
          <ul className="space-y-2 mb-4">
            {patrolTypes.map((t) => (
              <li key={t.id} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2">
                <span className="text-sm text-gray-800">{t.name}</span>
                <button
                  onClick={() => handleDeleteType(t.id, t.name)}
                  className="text-xs text-red-500 hover:text-red-700 transition-colors"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-400 mb-4">No patrol types defined yet.</p>
        )}
        <form onSubmit={handleAddType} className="flex gap-2">
          <input
            type="text"
            value={newTypeName}
            onChange={(e) => setNewTypeName(e.target.value)}
            placeholder="New type name"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <button
            type="submit"
            disabled={typeAdding || !newTypeName.trim()}
            className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-60 transition-colors"
          >
            {typeAdding ? 'Adding…' : 'Add'}
          </button>
        </form>
        {typeError && (
          <p className="text-sm text-red-600 mt-2">{typeError}</p>
        )}
      </section>

      {/* App setup */}
      <section className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-800 mb-1">Patroller app setup</h2>
        <p className="text-xs text-gray-400 mb-4">
          Two steps for each patroller's phone: install the app, then open the setup link
          once so the app knows it belongs to this site. After that they just sign in.
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1">
          1. Setup link <span className="font-normal text-gray-400">— send this to each patroller</span>
        </label>
        <p className="text-xs text-gray-400 mb-3">
          Opening this on the phone points the installed app at this site. Safe to send by
          WhatsApp: it identifies the site, but signing in still needs a patroller's own
          credentials.
        </p>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={setupUrl}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono bg-gray-50 text-gray-700 select-all"
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={handleCopySetupUrl}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            {setupCopied ? '✓ Copied' : 'Copy'}
          </button>
        </div>

        <label className="block text-sm font-medium text-gray-700 mb-1 mt-6">
          Site ID <span className="font-normal text-gray-400">— only needed if entering it by hand</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={estateId ?? ''}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono bg-gray-50 text-gray-700 select-all"
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={handleCopyEstateId}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-100">
          <label className="block text-sm font-medium text-gray-700 mb-1">2. App download</label>
          <p className="text-xs text-gray-400 mb-3">
            Open this on the phone itself, or send it to patrollers. Android asks for permission
            to install from this source the first time — expected for an app installed outside
            the Play Store. One app serves every site; the setup link above is what tells it
            which one.
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={apkUrl}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono bg-gray-50 text-gray-700 select-all"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={handleCopyApkUrl}
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              {apkCopied ? '✓ Copied' : 'Copy'}
            </button>
            <a
              href={UNIVERSAL_APK_PATH}
              download
              className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors whitespace-nowrap"
            >
              Download
            </a>
          </div>
        </div>
      </section>

      {/* Subscription */}
      {sub && (
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-1">Subscription</h2>
          <p className="text-xs text-gray-400 mb-4">
            {PRICE} per month per site, covering up to 10 patrollers.{BILLING_NOTE}
          </p>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm text-gray-500">Status:</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              sub.subscription_status === 'active' ? 'bg-green-100 text-green-800'
                : sub.subscription_status === 'cancelled' ? 'bg-gray-200 text-gray-700'
                : 'bg-amber-100 text-amber-800'
            }`}>
              {sub.subscription_status}
            </span>
          </div>
          {subError && <p className="text-sm text-red-600 mb-3">{subError}</p>}
          {sub.cancellable && sub.subscription_status !== 'cancelled' ? (
            <button
              onClick={handleCancelSubscription}
              disabled={subBusy}
              className="px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-medium hover:bg-red-50 disabled:opacity-60 transition-colors"
            >
              {subBusy ? 'Cancelling…' : 'Cancel subscription'}
            </button>
          ) : (
            <p className="text-xs text-gray-400">
              {sub.subscription_status === 'cancelled'
                ? 'Your subscription is cancelled. Contact us to start it again.'
                : 'No recurring subscription is attached to this site — contact us about billing.'}
            </p>
          )}
        </section>
      )}

      {/* Feedback */}
      <section className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-800 mb-1">Send feedback</h2>
        <p className="text-xs text-gray-400 mb-4">
          Bugs, requests, or anything that looks wrong. Goes straight to the PatrolIt team —
          not monitored around the clock, so please use an alarm for anything urgent.
        </p>
        <form onSubmit={handleSendFeedback} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={fbCategory}
              onChange={(e) => setFbCategory(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="general">General</option>
              <option value="bug">Something is broken</option>
              <option value="request">Feature request</option>
              <option value="billing">Billing / account</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              value={fbMessage}
              onChange={(e) => setFbMessage(e.target.value)}
              rows={5}
              placeholder="What happened, and what did you expect instead?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          {fbError && <p className="text-sm text-red-600">{fbError}</p>}
          {fbSent && (
            <p className="text-sm text-green-700">
              Thanks — we have it. We will come back to you by email.
            </p>
          )}
          <button
            type="submit"
            disabled={fbSending || !fbMessage.trim()}
            className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-60 transition-colors"
          >
            {fbSending ? 'Sending…' : 'Send feedback'}
          </button>
        </form>
      </section>

      {/* Change password */}
      <section className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-800 mb-4">Change password</h2>
        <form onSubmit={handleChangePassword} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current password</label>
            <ShowHideInput value={currentPassword} onChange={setCurrentPassword} autoComplete="current-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
            <ShowHideInput value={newPassword} onChange={setNewPassword} placeholder="Min 8 characters" autoComplete="new-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
            <ShowHideInput value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" />
          </div>
          {pwError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{pwError}</p>
          )}
          <div className="flex items-center gap-3 pt-1">
            <button type="submit" disabled={pwSaving}
              className="px-5 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-60 transition-colors">
              {pwSaving ? 'Saving…' : 'Update password'}
            </button>
            {pwSaved && <span className="text-sm text-green-600 font-medium">✓ Password updated</span>}
          </div>
        </form>
      </section>

      {/* Subscription — hidden until payment integration is configured
      <section>...</section>
      */}
      {estate && (
        <p className="text-xs text-gray-400">
          Site status: <span className="capitalize font-medium">{estate.subscription_status}</span>
        </p>
      )}
    </div>
  );
}
